// P1-023: Unified Project Panel — single list with 3-step progress indicator
import { useProjectStore } from '@/shared/stores/projectStore';
import { usePhaseStore } from '@/shared/stores/phaseStore';
import { MOCK_PREPARE_RESULT } from '../__mocks__/mock-prepare-result';
import type { ProjectArchive } from '@/shared/types/phase-contracts';

type ProjectStep = 'contract' | 'battle' | 'maintenance';

function getStepStatus(p: ProjectArchive, step: ProjectStep): 'done' | 'active' | 'locked' {
  switch (step) {
    case 'contract':
      return p.contractOutput !== null ? 'done' : 'active';
    case 'battle':
      if (p.battleResult !== null) return 'done';
      if (p.contractOutput !== null) return 'active';
      return 'locked';
    case 'maintenance':
      if (p.maintenanceResult !== null) return 'done';
      if (p.battleResult !== null) return 'active';
      return 'locked';
  }
}

const STEPS: { key: ProjectStep; label: string; icon: string }[] = [
  { key: 'contract', label: '签', icon: '/assets/icons/nav/icon_nav_orders.webp' },
  { key: 'battle', label: '战', icon: '/assets/icons/nav/icon_nav_execute.webp' },
  { key: 'maintenance', label: '维', icon: '/assets/icons/nav/icon_nav_settings.webp' },
];

function getProjectMeta(p: ProjectArchive): string {
  if (p.maintenanceResult !== null) {
    const mr = p.maintenanceResult;
    return `HEALTH: ${mr.baseHealthRemaining}% · ${mr.outcome === 'VICTORY' ? 'STABLE' : `${mr.enemiesKilled} FIXED`}`;
  }
  if (p.battleResult !== null) {
    const br = p.battleResult;
    return `BATTLE: ${br.battleOutcome === 'VICTORY' ? 'S' : 'C'} · BUGS: ${br.battleStats.overloadEvents}`;
  }
  if (p.contractOutput !== null) {
    return `CONTRACT: ${p.contractOutput.success ? 'A' : 'C'} · BONUS: +${Math.round(p.contractOutput.score * 2)}% ₣`;
  }
  if (p.contractResult?.project) {
    return `CLIENT: ${p.contractResult.project.name} · ₣${p.contractResult.project.budget.toLocaleString()}`;
  }
  return `ID: ${p.projectId.slice(0, 8)}`;
}

interface ProjectPanelProps {
  onStartContract?: (projectId: string) => void;
  onStartBattle?: (projectId: string) => void;
  onStartMaintenance?: (projectId: string) => void;
}

export function ProjectPanel({ onStartContract, onStartBattle, onStartMaintenance }: ProjectPanelProps) {
  const createProject = useProjectStore((s) => s.createProject);
  const prepareResult = usePhaseStore((s) => s.currentPrepare);
  const projects = useProjectStore((s) => s.projects);

  const handleNewProject = () => {
    const snapshot = prepareResult ?? MOCK_PREPARE_RESULT;
    const projectId = createProject(snapshot);
    onStartContract?.(projectId);
  };

  const handleStepClick = (p: ProjectArchive, step: ProjectStep) => {
    const status = getStepStatus(p, step);
    if (status !== 'active') return;

    switch (step) {
      case 'contract':
        onStartContract?.(p.projectId);
        break;
      case 'battle':
        onStartBattle?.(p.projectId);
        break;
      case 'maintenance':
        onStartMaintenance?.(p.projectId);
        break;
    }
  };

  const allProjects = Object.values(projects);
  const activeProjects = allProjects.filter((p) => p.status === 'active');
  const completedProjects = allProjects.filter((p) => p.status === 'completed');

  return (
    <div className="hub-panel project-panel">
      {/* Title row */}
      <div className="hub-panel__title-row">
        <h2 className="hub-panel__title">项目 PROJECTS</h2>
        <button className="hub-panel__action-btn" onClick={handleNewProject}>
          + NEW PROJECT
        </button>
      </div>

      {/* Content */}
      {allProjects.length === 0 ? (
        <div className="hub-panel__empty">
          还没有项目，点击上方按钮开始第一个合同
        </div>
      ) : (
        <div className="hub-panel__lists">
          {/* Active */}
          {activeProjects.length > 0 && (
            <div className="hub-panel__section">
              <div className="hub-panel__section-title">IN PROGRESS</div>
              <div className="hub-panel__list">
                {activeProjects.map((p) => (
                  <div key={p.projectId} className="hub-panel__item hub-panel__item--active">
                    <div className="hub-panel__item-info">
                      <div className="hub-panel__item-name">
                        {p.projectName || `项目 ${p.projectId.slice(0, 6)}`}
                      </div>
                      <div className="hub-panel__item-meta">
                        {getProjectMeta(p)}
                      </div>
                    </div>
                    <div className="project-steps">
                      {STEPS.map((step, i) => {
                        const status = getStepStatus(p, step.key);
                        return (
                          <div key={step.key} className="project-steps__wrapper">
                            <button
                              className={`project-steps__step project-steps__step--${status}`}
                              onClick={() => handleStepClick(p, step.key)}
                              disabled={status === 'locked'}
                              title={`${step.label} — ${status === 'done' ? '已完成' : status === 'active' ? '可执行' : '未解锁'}`}
                            >
                              <img
                                className="project-steps__icon"
                                src={step.icon}
                                alt={step.label}
                              />
                            </button>
                            {i < STEPS.length - 1 && (
                              <div className={`project-steps__connector ${status === 'done' ? 'project-steps__connector--done' : ''}`} />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Completed */}
          {completedProjects.length > 0 && (
            <div className="hub-panel__section">
              <div className="hub-panel__section-title">COMPLETED</div>
              <div className="hub-panel__list">
                {completedProjects.map((p) => (
                  <div key={p.projectId} className="hub-panel__item hub-panel__item--completed">
                    <div className="hub-panel__item-info">
                      <div className="hub-panel__item-name">
                        {p.projectName || `项目 ${p.projectId.slice(0, 6)}`}
                      </div>
                      <div className="hub-panel__item-meta">
                        {getProjectMeta(p)}
                      </div>
                    </div>
                    <div className="project-steps">
                      {STEPS.map((step, i) => (
                        <div key={step.key} className="project-steps__wrapper">
                          <button className="project-steps__step project-steps__step--done" disabled>
                            <img className="project-steps__icon" src={step.icon} alt={step.label} />
                          </button>
                          {i < STEPS.length - 1 && (
                            <div className="project-steps__connector project-steps__connector--done" />
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
